<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { coreServiceRegistry } from "@core/services/register"
import { formatIdSource } from "@core/helpers"
import type { BaseSummaryLine } from "@core/components/base/summary/BaseSummary.vue"
import type { OwnerOutput } from "@core/composables"
import type { Address } from "@types"
import type {
    ConsentRequestSignResponse,
    CreateConsentRequestChangeOwnerBody,
} from "@core/services/consentrequest.service"

// ================================
// TYPES & INTERFACES
// ================================

type InvitationResponse = ConsentRequestSignResponse & { url?: string }

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /** Identifiant Mongo de la source (compteur) */
        sourceMongoId: string
        /** Adresse du bâtiment (affichage récap) */
        address: Address | Record<string, unknown>
        /** Numéro PDL / PCE affiché */
        idsource: string
        /** Type pdl | pce */
        type?: string
        /** Nouveau titulaire */
        owner: OwnerOutput
        /** Date première donnée (PCE), YYYY-MM-DD */
        dateFromData?: string | null
    }>(),
    {
        type: "pdl",
        dateFromData: null,
    }
)

const emit = defineEmits<{
    back: []
    sent: [payload: { _id_consentrequest: string; url: string }]
}>()

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    consentRequest?: typeof coreServiceRegistry.consentRequest
}>("services")
const consentRequestService =
    injectedServices?.consentRequest ?? coreServiceRegistry.consentRequest

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Demande de changement de titulaire",
            subtitle:
                "Un e-mail sera envoyé au nouveau titulaire pour qu'il accepte la demande.",
            generate: "Générer la demande",
            back: "Retour",
            field_titulaire_pro: "Titulaire (professionnel)",
            field_titulaire_particular: "Titulaire (particulier)",
            field_company: "Entreprise",
            field_address: "Adresse",
            field_email: "E-mail",
            field_idsource_pdl: "Numéro PDL",
            field_idsource_pce: "Numéro PCE",
            field_date_pce: "Première date de donnée",
        },
    },
})

// ================================
// STATE
// ================================

const invitationResult = ref<InvitationResponse | null>(null)

// ================================
// HELPERS
// ================================

/**
 * Construit le corps POST attendu par l’API métier (validateur change-owner).
 */
function buildChangeOwnerPostBody(): CreateConsentRequestChangeOwnerBody {
    const isPro = !!props.owner.company?.name?.trim()
    const hasUserId = !!props.owner.user._id?.trim()

    const source: CreateConsentRequestChangeOwnerBody["source"] = {
        _id_source: props.sourceMongoId,
    }
    const rawDate = props.dateFromData?.trim()
    if (rawDate && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
        source.date_from_data = rawDate
    }

    const user: CreateConsentRequestChangeOwnerBody["user"] = {
        firstname: props.owner.user.firstname,
        lastname: props.owner.user.lastname,
        username: props.owner.user.username,
        ...(hasUserId ? { _id_user: props.owner.user._id } : {}),
    }

    if (isPro && props.owner.company) {
        user.company = {
            name: props.owner.company.name,
            type: props.owner.company.type?.trim() || "entreprise",
        }
        if (props.owner.company._id) {
            user.company._id = props.owner.company._id
        }
        if (props.owner.company.address) {
            user.company.address = props.owner.company.address
        }
    }

    return {
        source,
        user,
        mail: true,
    }
}

// ================================
// FETCH
// ================================

async function sendRequestFn(): Promise<void> {
    const body = buildChangeOwnerPostBody()
    const result =
        await consentRequestService.createConsentRequestChangeOwner(body)
    invitationResult.value = result
}

const {
    fetch: fetchSend,
    isFetching,
    hasError,
    error: fetchError,
    isSuccess,
} = useFetchWrapper(sendRequestFn)

const getErrorMessage = computed(() => {
    const err = fetchError.value
    if (!err) return ""
    return (
        (err as Error & { response?: { data?: { message?: string } } })
            ?.response?.data?.message ??
        err.message ??
        "Une erreur est survenue."
    )
})

watch(isSuccess, (success) => {
    if (success && invitationResult.value) {
        const res = invitationResult.value
        const id = res._id ?? ""
        const path = `/demande/changement-titulaire/${id}`
        const url =
            res.url?.trim() ||
            (typeof window !== "undefined"
                ? `${window.location.origin}${path}`
                : path)
        emit("sent", {
            _id_consentrequest: id,
            url,
        })
        invitationResult.value = null
    }
})

// ================================
// COMPUTED
// ================================

const getFields = computed<BaseSummaryLine[]>(() => {
    const ownerLabel = props.owner?.company?.name
        ? t("field_titulaire_pro")
        : t("field_titulaire_particular")
    const lines: BaseSummaryLine[] = [
        {
            label: ownerLabel,
            value: `${props.owner?.user?.firstname ?? ""} ${props.owner?.user?.lastname ?? ""}`.trim(),
        },
        {
            label: t("field_address"),
            value:
                (props.address as Address)?.inline_name ??
                (props.address as Address)?.street ??
                "",
        },
        {
            label: t("field_email"),
            value: props.owner?.user?.username ?? "",
        },
        {
            label:
                props.type === "pce"
                    ? t("field_idsource_pce")
                    : t("field_idsource_pdl"),
            value: formatIdSource(props.idsource, props.type),
        },
    ]
    if (props.owner?.company?.name) {
        lines.unshift({
            label: t("field_company"),
            value: props.owner.company.name,
        })
    }
    if (
        props.type === "pce" &&
        props.dateFromData &&
        /^\d{4}-\d{2}-\d{2}$/.test(props.dateFromData)
    ) {
        lines.push({
            label: t("field_date_pce"),
            value: props.dateFromData,
        })
    }
    return lines
})

// ================================
// HANDLERS
// ================================

function onBack(): void {
    emit("back")
}

function onGenerate(): void {
    fetchSend()
}
</script>

<template>
    <div class="source-invitation-change-owner flex flex-col gap-4">
        <header class="w-full text-center flex flex-col gap-4">
            <h2 class="text-lg font-semibold text-gray-700 text-center">
                {{ t("title") }}
            </h2>
            <p class="text-sm text-gray-600">
                {{ t("subtitle") }}
            </p>
        </header>

        <BaseSummary :lines="getFields" variant="grey" />

        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="t('back')"
                severity="secondary"
                :disabled="isFetching"
                class="min-w-[10rem] w-full md:w-auto"
                @click="onBack"
            />
            <BaseButton
                :label="t('generate')"
                :loading="isFetching"
                :disabled="!getFields.length || isFetching"
                class="min-w-[10rem] w-full md:w-auto"
                @click="onGenerate"
            />
        </div>

        <BaseMessage
            v-if="hasError && getErrorMessage"
            severity="error"
            :closable="false"
            class="w-full"
        >
            {{ getErrorMessage }}
        </BaseMessage>
    </div>
</template>
